import React from 'react';
import { Link } from 'react-router-dom';
import { FileImage, CheckCircle, AlertCircle } from 'lucide-react';
export function VectorArt() {
  const sidebarLinks = [{
    name: 'FAQs',
    path: '/helpful-info/faqs'
  }, {
    name: 'Compliance',
    path: '/helpful-info/compliance'
  }, {
    name: 'Screen Printing Advice',
    path: '/helpful-info/screen-printing-advice'
  }, {
    name: 'What is Vector Art?',
    path: '/helpful-info/vector-art'
  }, {
    name: 'The Wear Haus Way',
    path: '/helpful-info/wear-haus-way'
  }, {
    name: 'Terms and Conditions',
    path: '/helpful-info/terms-and-conditions'
  }, {
    name: 'Privacy Policy',
    path: '/helpful-info/privacy-policy'
  }];
  return <div className="min-h-screen bg-gray-50" data-id="element-810">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-16" data-id="element-811">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-812">
          <FileImage className="w-16 h-16 text-white mx-auto mb-4" data-id="element-813" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-814">
            What is Vector Art?
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-815">
            Understanding vector graphics and why they're essential for quality
            printing
          </p>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-816">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-817">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0" data-id="element-818">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24" data-id="element-819">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-820">
                Helpful Info
              </h3>
              <nav className="space-y-2" data-id="element-821">
                {sidebarLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === link.path ? 'bg-wh-pink text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-822">
                    {link.name}
                  </Link>)}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1" data-id="element-823">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-824">
              <div className="prose prose-lg max-w-none" data-id="element-825">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-826">
                  Understanding Vector Graphics
                </h2>

                <p className="text-gray-700 leading-relaxed mb-6" data-id="element-827">
                  Vector art is a type of digital graphic made up of
                  mathematical paths and points rather than pixels. Unlike
                  raster images (like JPEGs or PNGs), vector graphics can be
                  scaled to any size without losing quality, making them perfect
                  for printing on everything from business cards to billboards.
                </p>

                <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8" data-id="element-828">
                  <div className="flex items-start gap-3" data-id="element-829">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-830" />
                    <div data-id="element-831">
                      <h3 className="text-lg font-bold text-green-900 mb-2" data-id="element-832">
                        Why Vector Art Matters
                      </h3>
                      <p className="text-green-800" data-id="element-833">
                        For screen printing and embroidery, vector art ensures
                        crisp, clean lines and accurate color reproduction at
                        any size. It's the industry standard for professional
                        printing.
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-834">
                  Common Vector File Formats
                </h3>
                <ul className="space-y-3 mb-8" data-id="element-835">
                  <li className="flex items-start gap-3" data-id="element-836">
                    <CheckCircle className="w-5 h-5 text-wh-pink flex-shrink-0 mt-1" data-id="element-837" />
                    <span data-id="element-838">
                      <strong data-id="element-839">.AI (Adobe Illustrator)</strong> - The most common
                      professional format
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-840">
                    <CheckCircle className="w-5 h-5 text-wh-pink flex-shrink-0 mt-1" data-id="element-841" />
                    <span data-id="element-842">
                      <strong data-id="element-843">.EPS (Encapsulated PostScript)</strong> -
                      Universal format compatible with most design software
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-844">
                    <CheckCircle className="w-5 h-5 text-wh-pink flex-shrink-0 mt-1" data-id="element-845" />
                    <span data-id="element-846">
                      <strong data-id="element-847">.PDF (Portable Document Format)</strong> - Can
                      contain vector graphics when created properly
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-848">
                    <CheckCircle className="w-5 h-5 text-wh-pink flex-shrink-0 mt-1" data-id="element-849" />
                    <span data-id="element-850">
                      <strong data-id="element-851">.SVG (Scalable Vector Graphics)</strong> -
                      Web-friendly vector format
                    </span>
                  </li>
                </ul>

                <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-852">
                  Vector vs. Raster Graphics
                </h3>
                <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-853">
                  <div className="bg-green-50 rounded-lg p-6" data-id="element-854">
                    <h4 className="text-lg font-bold text-green-900 mb-3" data-id="element-855">
                      ✓ Vector Graphics
                    </h4>
                    <ul className="space-y-2 text-green-800" data-id="element-856">
                      <li data-id="element-857">• Infinitely scalable</li>
                      <li data-id="element-858">• Small file sizes</li>
                      <li data-id="element-859">• Perfect for logos and text</li>
                      <li data-id="element-860">• Easy to edit colors and shapes</li>
                      <li data-id="element-861">• Ideal for printing</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 rounded-lg p-6" data-id="element-862">
                    <h4 className="text-lg font-bold text-red-900 mb-3" data-id="element-863">
                      ✗ Raster Graphics (JPG, PNG)
                    </h4>
                    <ul className="space-y-2 text-red-800" data-id="element-864">
                      <li data-id="element-865">• Lose quality when enlarged</li>
                      <li data-id="element-866">• Larger file sizes</li>
                      <li data-id="element-867">• Best for photos</li>
                      <li data-id="element-868">• Difficult to edit precisely</li>
                      <li data-id="element-869">• Limited for professional printing</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8" data-id="element-870">
                  <div className="flex items-start gap-3" data-id="element-871">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-872" />
                    <div data-id="element-873">
                      <h3 className="text-lg font-bold text-yellow-900 mb-2" data-id="element-874">
                        Don't Have Vector Art?
                      </h3>
                      <p className="text-yellow-800 mb-3" data-id="element-875">
                        No problem! Our in-house graphic design team can convert
                        your logo or artwork to vector format, or create custom
                        vector designs from scratch.
                      </p>
                      <Link to="/services/graphic-design" className="inline-block text-yellow-900 font-semibold hover:text-yellow-700 underline" data-id="element-876">
                        Learn about our Graphic Design services →
                      </Link>
                    </div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-877">
                  How to Provide Vector Art
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-878">
                  When submitting artwork for printing, please provide:
                </p>
                <ul className="space-y-3 mb-8" data-id="element-879">
                  <li className="flex items-start gap-3" data-id="element-880">
                    <CheckCircle className="w-5 h-5 text-wh-teal flex-shrink-0 mt-1" data-id="element-881" />
                    <span data-id="element-882">
                      Original vector files (.AI, .EPS, or .PDF with embedded
                      fonts)
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-883">
                    <CheckCircle className="w-5 h-5 text-wh-teal flex-shrink-0 mt-1" data-id="element-884" />
                    <span data-id="element-885">All fonts converted to outlines or paths</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-886">
                    <CheckCircle className="w-5 h-5 text-wh-teal flex-shrink-0 mt-1" data-id="element-887" />
                    <span data-id="element-888">Colors specified in Pantone or CMYK values</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-889">
                    <CheckCircle className="w-5 h-5 text-wh-teal flex-shrink-0 mt-1" data-id="element-890" />
                    <span data-id="element-891">
                      Artwork sized appropriately for your intended use
                    </span>
                  </li>
                </ul>

                <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-xl p-8 text-white text-center" data-id="element-892">
                  <h3 className="text-2xl font-bold mb-4" data-id="element-893">
                    Need Help with Your Artwork?
                  </h3>
                  <p className="text-white/90 mb-6 max-w-2xl mx-auto" data-id="element-894">
                    Our design team is here to help! Whether you need artwork
                    converted to vector format or a completely new design, we've
                    got you covered.
                  </p>
                  <Link to="/contact" className="inline-block bg-white text-wh-pink font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-895">
                    Contact Our Design Team
                  </Link>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>;
}